import React from 'react';
import { Alert, Spinner, Badge } from 'react-bootstrap';

const PendingPaymentIndicator = ({ 
  status, 
  paymentId, 
  transactionHash, 
  onDismiss,
  compact = false 
}) => {
  const getStatusInfo = () => {
    switch (status) {
      case 'polling_started':
        return {
          variant: 'info',
          icon: 'fas fa-clock',
          title: 'Payment Submitted',
          message: 'Your cryptocurrency payment has been submitted. We\'re monitoring the blockchain for confirmation.',
          showSpinner: true
        };
      
      case 'pending':
        return {
          variant: 'warning',
          icon: 'fas fa-hourglass-half',
          title: 'Payment Pending',
          message: 'Your payment is being processed on the blockchain. This typically takes 10-20 minutes.',
          showSpinner: true
        };
      
      case 'completed':
        return {
          variant: 'success',
          icon: 'fas fa-check-circle',
          title: 'Payment Confirmed!',
          message: 'Your cryptocurrency payment has been confirmed and your subscription has been updated.',
          showSpinner: false
        };
      
      case 'expired':
        return {
          variant: 'danger',
          icon: 'fas fa-times-circle',
          title: 'Payment Expired',
          message: 'Your payment window has expired. Please try again or contact support if you believe this is an error.',
          showSpinner: false
        };
      
      case 'error':
        return {
          variant: 'danger',
          icon: 'fas fa-exclamation-triangle',
          title: 'Payment Error',
          message: 'There was an issue checking your payment status. We\'ll continue monitoring in the background.',
          showSpinner: true
        };
      
      default:
        return {
          variant: 'info',
          icon: 'fas fa-info-circle',
          title: 'Payment Status Unknown',
          message: 'Checking payment status...',
          showSpinner: true
        };
    }
  };

  const statusInfo = getStatusInfo();

  if (compact) {
    return (
      <div className="pending-payment-compact d-flex align-items-center">
        <Badge 
          variant={statusInfo.variant === 'warning' ? 'warning' : statusInfo.variant}
          className="d-flex align-items-center py-2 px-3"
        >
          {statusInfo.showSpinner && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="mr-2"
              style={{ width: '1rem', height: '1rem' }}
            />
          )}
          <i className={`${statusInfo.icon} mr-2`}></i>
          {statusInfo.title}
        </Badge>
        {transactionHash && (
          <small className="ml-2 text-muted">
            TX: {transactionHash.substring(0, 8)}...
          </small>
        )}
      </div>
    );
  }

  return (
    <Alert 
      variant={statusInfo.variant} 
      dismissible={status === 'completed' || status === 'expired'}
      onClose={onDismiss}
      className="pending-payment-alert"
    >
      <div className="d-flex align-items-start">
        <div className="mr-3 mt-1">
          {statusInfo.showSpinner ? (
            <Spinner
              animation="border"
              size="sm"
              variant={statusInfo.variant === 'info' ? 'primary' : statusInfo.variant}
            />
          ) : (
            <i className={`${statusInfo.icon} fa-lg`}></i>
          )}
        </div>
        <div className="flex-grow-1">
          <Alert.Heading className="h6 mb-2">
            {statusInfo.title}
          </Alert.Heading>
          <p className="mb-2">{statusInfo.message}</p>
          
          {paymentId && (
            <div className="small text-muted">
              <strong>Payment ID:</strong> {paymentId}
            </div>
          )}
          
          {transactionHash && (
            <div className="small text-muted">
              <strong>Transaction:</strong> 
              <a 
                href={`https://blockchain.info/tx/${transactionHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1"
              >
                {transactionHash}
              </a>
            </div>
          )}
          
          {status === 'pending' && (
            <div className="mt-2 small">
              <i className="fas fa-info-circle mr-1"></i>
              You will receive an email confirmation once the payment is processed.
              <br />
              <i className="fas fa-sync-alt mr-1"></i>
              We're checking the blockchain every few minutes for updates.
            </div>
          )}
          
          {status === 'error' && (
            <div className="mt-2 small">
              <i className="fas fa-info-circle mr-1"></i>
              If this issue persists, please contact support with your payment ID.
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .pending-payment-alert {
          border-left: 4px solid;
        }
        
        .pending-payment-alert .alert-info {
          border-left-color: #17a2b8;
        }
        
        .pending-payment-alert .alert-warning {
          border-left-color: #ffc107;
        }
        
        .pending-payment-alert .alert-success {
          border-left-color: #28a745;
        }
        
        .pending-payment-alert .alert-danger {
          border-left-color: #dc3545;
        }

        .pending-payment-compact .badge {
          font-size: 0.875rem;
        }
      `}</style>
    </Alert>
  );
};

export default PendingPaymentIndicator;